/**
 * AI Test Arena
 * Backend dev page for running AI vs AI tournaments, ELO ranking, and blunder analysis.
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './AIArena.module.css';
import {
  ArenaService,
  ArenaState,
  ArenaVariant,
  VariantRating,
  BlunderRecord,
  MatchResult,
} from '../../services/AIArena/ArenaService';
import {
  AIModel,
  AI_MODEL_CONFIG,
  TimeFrame,
  TIME_FRAME_CONFIG,
} from '../../services/zodiacEngine/AI/Core/AIVariants';
import { AIArenaFirebaseService } from '../../services/AIArena/AIArenaFirebaseService';
import { AIModelRatingDoc, AIGameDoc, NotableMove, BlunderVerdict } from '../../services/AIArena/AIAnalyticsTypes';
import { NAMED_VARIANTS, resolveVariantDescriptor } from '../../services/AIArena/AIArenaConstants';
import { GameRecordDocument } from '../../services/firebase/gameRecordService';
import { AIModelPieceStatsDoc } from '../../services/bots/pieceStats/PieceStatsTypes';
import ArenaReplayPanel from './ArenaReplayPanel';
import BlunderTestSuitePanel from './BlunderTestSuitePanel';
import { playUISound } from '../../utils/UISoundEffects';

// Models available in the arena
const IMPLEMENTED_MODELS: AIModel[] = [
  AIModel.SENTINEL,
  AIModel.CREATURE,
];

interface ArenaChip {
  key: string;           // e.g. "gaia-fast"
  model: AIModel;
  timeFrame: TimeFrame;
  label: string;         // Display name e.g. "Wisp" or "Hunter (2s)"
  timeLimit: number;
}

// Build all arena chips: every implemented model × every time frame
const ALL_ARENA_CHIPS: ArenaChip[] = [];
for (const model of IMPLEMENTED_MODELS) {
  for (const tf of Object.values(TimeFrame)) {
    const key = `${model}-${tf}`;
    const tfConfig = TIME_FRAME_CONFIG[tf];
    const named = NAMED_VARIANTS[key];
    const label = named || `${AI_MODEL_CONFIG[model].displayName} (${tfConfig.timeLimit / 1000}s)`;
    ALL_ARENA_CHIPS.push({ key, model, timeFrame: tf, label, timeLimit: tfConfig.timeLimit });
  }
}

// Group chips by time frame for UI display
const CHIPS_BY_TIME: Record<TimeFrame, ArenaChip[]> = {
  [TimeFrame.FAST]: ALL_ARENA_CHIPS.filter(c => c.timeFrame === TimeFrame.FAST),
  [TimeFrame.STANDARD]: ALL_ARENA_CHIPS.filter(c => c.timeFrame === TimeFrame.STANDARD),
  [TimeFrame.DEEP]: ALL_ARENA_CHIPS.filter(c => c.timeFrame === TimeFrame.DEEP),
};

/** Resolve a variant ID like "alchemist-deep" to its display name like "Oracle" */
function resolveVariantName(variantId: string): string {
  return NAMED_VARIANTS[variantId] || variantId;
}

/** Display-friendly win condition label */
function formatWinCondition(wc: string): string {
  switch (wc) {
    case 'race': return 'Race';
    case 'capture': return 'Capture';
    case 'repetition': return 'Repetition';
    case 'stalemate': return 'Stalemate';
    default: return wc;
  }
}

/** Parse a Firestore timestamp (could be Date, Firestore Timestamp, or string) to ms */
function parseTimestamp(ts: any): number {
  if (ts instanceof Date) return ts.getTime();
  if (ts && typeof ts.toMillis === 'function') return ts.toMillis(); // Firestore Timestamp
  if (ts && typeof ts.seconds === 'number') return ts.seconds * 1000; // Firestore Timestamp plain object
  if (typeof ts === 'string') return new Date(ts).getTime();
  return Date.now();
}

/** Convert an AIGameDoc to a GameRecordDocument for ReplayViewer */
function aiGameToReplayRecord(game: AIGameDoc & { _id: string }): GameRecordDocument | null {
  if (!game.fenHistory || game.fenHistory.length <= 1) return null;
  const lightName = resolveVariantName(game.lightVariant);
  const darkName = resolveVariantName(game.darkVariant);
  return {
    id: game._id,
    lightPlayerId: game.lightVariant,
    darkPlayerId: game.darkVariant,
    lightPlayerUsername: lightName,
    darkPlayerUsername: darkName,
    lightKing: game.lightKing || '',
    darkKing: game.darkKing || '',
    fenHistory: game.fenHistory,
    evalHistory: game.evalHistory,
    result: game.winner,
    winCondition: game.winCondition,
    turnCount: game.moveCount,
    timestamp: parseTimestamp(game.timestamp),
    timeControlPreset: '',
    gameName: `${lightName} vs ${darkName}`,
  };
}

/** Convert an in-memory MatchResult to a GameRecordDocument for ReplayViewer */
function matchResultToReplayRecord(m: MatchResult, index: number): GameRecordDocument | null {
  if (!m.fenHistory || m.fenHistory.length <= 1) return null;
  const lightName = resolveVariantName(m.lightVariant);
  const darkName = resolveVariantName(m.darkVariant);
  return {
    id: `mem-${index}`,
    lightPlayerId: m.lightVariant,
    darkPlayerId: m.darkVariant,
    lightPlayerUsername: lightName,
    darkPlayerUsername: darkName,
    lightKing: m.lightKing || '',
    darkKing: m.darkKing || '',
    fenHistory: m.fenHistory,
    evalHistory: m.evalHistory,
    result: m.winner,
    winCondition: m.winCondition,
    turnCount: m.moves,
    timestamp: Date.now(),
    timeControlPreset: '',
    gameName: `${lightName} vs ${darkName}`,
  };
}

function AIArena() {
  const navigate = useNavigate();
  const arenaRef = useRef<ArenaService>(new ArenaService());
  const stopCooldownRef = useRef(false);

  // Config state — individual variant selection (e.g. "gaia-fast", "sentinel-deep")
  const [selectedVariants, setSelectedVariants] = useState<Set<string>>(
    new Set(['sentinel-standard', 'creature-standard'])
  );
  const [gamesPerMatchup, setGamesPerMatchup] = useState(2);
  const [concurrency, setConcurrency] = useState(1);

  // Arena state
  const [arenaState, setArenaState] = useState<ArenaState>(arenaRef.current.getState());

  // Blunder expansion
  const [expandedBlunderVariant, setExpandedBlunderVariant] = useState<string | null>(null);

  // Firebase persistent data
  const [firebaseRatings, setFirebaseRatings] = useState<(AIModelRatingDoc & { _docId: string })[]>([]);
  const [firebaseLoading, setFirebaseLoading] = useState(true);

  // Head-to-head state
  const [h2hVariantA, setH2hVariantA] = useState('');
  const [h2hVariantB, setH2hVariantB] = useState('');
  const [h2hResult, setH2hResult] = useState<{ aWins: number; bWins: number; draws: number } | null>(null);

  // Blunder analysis state
  const [blunderGames, setBlunderGames] = useState<(AIGameDoc & { _id: string })[]>([]);
  const [blunderAnalysisLoading, setBlunderAnalysisLoading] = useState(false);
  const [selectedBlunder, setSelectedBlunder] = useState<{
    gameId: string;
    game: AIGameDoc;
    move: NotableMove;
    variantId: string;
  } | null>(null);
  const [feedbackNotes, setFeedbackNotes] = useState('');
  const [submittedFeedback, setSubmittedFeedback] = useState<Set<string>>(new Set());
  const [blunderFilter, setBlunderFilter] = useState<string>('all');

  // Replay state
  const [replayGame, setReplayGame] = useState<GameRecordDocument | null>(null);
  const [standaloneFen, setStandaloneFen] = useState<{ fen: string; lightKing: string; darkKing: string } | null>(null);
  const [blunderRefreshKey, setBlunderRefreshKey] = useState(0);

  // Model Profile state
  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null);
  const [profileGames, setProfileGames] = useState<(AIGameDoc & { _id: string })[]>([]);
  const [profileLoading, setProfileLoading] = useState(false);
  const [pieceSelectionStats, setPieceSelectionStats] = useState<AIModelPieceStatsDoc | null>(null);

  const selectedProfileRating = useMemo(() =>
    firebaseRatings.find(r => r._docId === selectedProfileId) || null,
    [selectedProfileId, firebaseRatings]
  );

  // Sort state for leaderboard tables
  const [lbSort, setLbSort] = useState<{ col: string; dir: 'asc' | 'desc' }>({ col: 'elo', dir: 'desc' });
  const [fbSort, setFbSort] = useState<{ col: string; dir: 'asc' | 'desc' }>({ col: 'elo', dir: 'desc' });

  const handleLbSort = useCallback((col: string) => {
    setLbSort(prev => prev.col === col ? { col, dir: prev.dir === 'desc' ? 'asc' : 'desc' } : { col, dir: 'desc' });
  }, []);
  const handleFbSort = useCallback((col: string) => {
    setFbSort(prev => prev.col === col ? { col, dir: prev.dir === 'desc' ? 'asc' : 'desc' } : { col, dir: 'desc' });
  }, []);

  const sortArrow = (col: string, sort: { col: string; dir: string }) =>
    sort.col === col ? <span className={styles.sortArrow}>{sort.dir === 'desc' ? ' ▼' : ' ▲'}</span> : null;

  // Subscribe to arena updates
  useEffect(() => {
    return arenaRef.current.subscribe(setArenaState);
  }, []);

  // Load persistent ratings from Firebase on mount and after tournament finishes
  const loadFirebaseRatings = useCallback(async () => {
    setFirebaseLoading(true);
    const ratings = await AIArenaFirebaseService.getModelRatings();
    setFirebaseRatings(ratings);
    setFirebaseLoading(false);
  }, []);

  useEffect(() => {
    loadFirebaseRatings();
  }, [loadFirebaseRatings]);

  // Reload Firebase data when tournament finishes
  useEffect(() => {
    if (arenaState.status === 'finished') {
      // Small delay to let Firebase writes complete
      const timer = setTimeout(loadFirebaseRatings, 2000);
      return () => clearTimeout(timer);
    }
  }, [arenaState.status, loadFirebaseRatings]);

  // Load profile data when a model is selected
  const loadProfileData = useCallback(async (variantId: string) => {
    setProfileLoading(true);
    setSelectedProfileId(variantId);
    setPieceSelectionStats(null);
    try {
      // Fetch game history and piece selection stats in parallel
      const aiModel = variantId.split('-')[0]; // e.g. "sentinel-standard" → "sentinel"
      const [games, pieceStats] = await Promise.all([
        AIArenaFirebaseService.getRecentGames(100, variantId),
        AIArenaFirebaseService.getPieceSelectionStats(aiModel),
      ]);
      setProfileGames(games);
      setPieceSelectionStats(pieceStats as AIModelPieceStatsDoc | null);
    } catch {
      setProfileGames([]);
      setPieceSelectionStats(null);
    }
    setProfileLoading(false);
  }, []);

  const handleH2hLookup = useCallback(async () => {
    if (!h2hVariantA || !h2hVariantB || h2hVariantA === h2hVariantB) return;
    const result = await AIArenaFirebaseService.getHeadToHead(h2hVariantA, h2hVariantB);
    setH2hResult({ aWins: result.aWins, bWins: result.bWins, draws: result.draws });
  }, [h2hVariantA, h2hVariantB]);

  const loadBlunderGames = useCallback(async () => {
    setBlunderAnalysisLoading(true);
    try {
      // Try Firebase first
      const fbGames = await AIArenaFirebaseService.getGamesWithBlunders(100);
      // Load existing feedback to mark already-reviewed
      const feedback = await AIArenaFirebaseService.getBlunderFeedback();
      const keys = new Set(feedback.map(f => `${f.gameId}-${f.moveNum}-${f.side}`));
      setSubmittedFeedback(keys);

      // Also include in-memory games from the current session that have blunders
      const memGames: (AIGameDoc & { _id: string })[] = arenaState.matchHistory
        .filter(m => m.notableMoves && m.notableMoves.some(n => n.type === 'blunder'))
        .map((m, i) => ({
          _id: `mem-${i}`,
          tournamentId: 'current-session',
          lightVariant: m.lightVariant,
          darkVariant: m.darkVariant,
          winner: m.winner,
          winCondition: m.winCondition,
          moveCount: m.moves,
          lightBlunders: m.lightBlunders,
          darkBlunders: m.darkBlunders,
          lightBrilliants: m.lightBrilliants,
          darkBrilliants: m.darkBrilliants,
          lightEloChange: 0,
          darkEloChange: 0,
          keyPositions: m.keyPositions || [],
          notableMoves: m.notableMoves || [],
          fenHistory: m.fenHistory || [],
          lightKing: m.lightKing || '',
          darkKing: m.darkKing || '',
          timestamp: new Date(),
        }));

      // Merge: Firebase games first, then in-memory games not already in Firebase
      const fbIds = new Set(fbGames.map(g => `${g.lightVariant}-${g.darkVariant}-${g.moveCount}`));
      const uniqueMemGames = memGames.filter(g =>
        !fbIds.has(`${g.lightVariant}-${g.darkVariant}-${g.moveCount}`)
      );
      setBlunderGames([...fbGames, ...uniqueMemGames]);
    } catch {
      // Firebase failed — fall back to in-memory data only
      const memGames: (AIGameDoc & { _id: string })[] = arenaState.matchHistory
        .filter(m => m.notableMoves && m.notableMoves.some(n => n.type === 'blunder'))
        .map((m, i) => ({
          _id: `mem-${i}`,
          tournamentId: 'current-session',
          lightVariant: m.lightVariant,
          darkVariant: m.darkVariant,
          winner: m.winner,
          winCondition: m.winCondition,
          moveCount: m.moves,
          lightBlunders: m.lightBlunders,
          darkBlunders: m.darkBlunders,
          lightBrilliants: m.lightBrilliants,
          darkBrilliants: m.darkBrilliants,
          lightEloChange: 0,
          darkEloChange: 0,
          fenHistory: m.fenHistory || [],
          lightKing: m.lightKing || '',
          darkKing: m.darkKing || '',
          keyPositions: m.keyPositions || [],
          notableMoves: m.notableMoves || [],
          timestamp: new Date(),
        }));
      setBlunderGames(memGames);
    }
    setBlunderAnalysisLoading(false);
  }, [arenaState.matchHistory]);

  const handleBlunderFeedback = useCallback(async (verdict: BlunderVerdict) => {
    if (!selectedBlunder) return;
    const key = `${selectedBlunder.gameId}-${selectedBlunder.move.moveNum}-${selectedBlunder.move.side}`;

    await AIArenaFirebaseService.saveBlunderFeedback({
      gameId: selectedBlunder.gameId,
      variantId: selectedBlunder.variantId,
      moveNum: selectedBlunder.move.moveNum,
      side: selectedBlunder.move.side,
      confidence: selectedBlunder.move.confidence,
      reasoning: selectedBlunder.move.reasoning,
      fen: selectedBlunder.move.fen,
      verdict,
      notes: feedbackNotes,
      timestamp: new Date(),
    });

    setSubmittedFeedback(prev => new Set(prev).add(key));
    setFeedbackNotes('');
    // Auto-advance to next unreviewed blunder
    advanceToNextBlunder(selectedBlunder.gameId, selectedBlunder.move.moveNum);
  }, [selectedBlunder, feedbackNotes]);

  const advanceToNextBlunder = useCallback((currentGameId: string, currentMoveNum: number) => {
    // Find next unreviewed blunder across all games
    for (const game of blunderGames) {
      const blunders = (game.notableMoves || []).filter(m => m.type === 'blunder');
      for (const move of blunders) {
        const key = `${game._id}-${move.moveNum}-${move.side}`;
        if (submittedFeedback.has(key)) continue;
        if (game._id === currentGameId && move.moveNum <= currentMoveNum) continue;
        // Filter check
        if (blunderFilter !== 'all') {
          const vid = move.side === 'light' ? game.lightVariant : game.darkVariant;
          if (vid !== blunderFilter) continue;
        }
        setSelectedBlunder({
          gameId: game._id,
          game,
          move,
          variantId: move.side === 'light' ? game.lightVariant : game.darkVariant,
        });
        return;
      }
    }
    setSelectedBlunder(null);
  }, [blunderGames, submittedFeedback, blunderFilter]);

  // Flatten all blunders for the list
  const allBlunders = React.useMemo(() => {
    const items: { gameId: string; game: AIGameDoc & { _id: string }; move: NotableMove; variantId: string }[] = [];
    for (const game of blunderGames) {
      for (const move of (game.notableMoves || [])) {
        if (move.type !== 'blunder') continue;
        const variantId = move.side === 'light' ? game.lightVariant : game.darkVariant;
        if (blunderFilter !== 'all' && variantId !== blunderFilter) continue;
        items.push({ gameId: game._id, game, move, variantId });
      }
    }
    return items;
  }, [blunderGames, blunderFilter]);

  // Unique variant IDs for filter dropdown
  const blunderVariantIds = React.useMemo(() => {
    const ids = new Set<string>();
    for (const game of blunderGames) {
      for (const move of (game.notableMoves || [])) {
        if (move.type !== 'blunder') continue;
        ids.add(move.side === 'light' ? game.lightVariant : game.darkVariant);
      }
    }
    return [...ids].sort();
  }, [blunderGames]);

  const [purging, setPurging] = useState(false);

  // Blunder lab: only clears feedback reviews so you can re-review
  const handleClearFeedback = useCallback(async () => {
    if (!window.confirm('Clear all blunder feedback? (Your verdict reviews will be reset — game data is untouched)')) return;
    setPurging(true);
    const count = await AIArenaFirebaseService.purgeBlunderFeedback();
    setSubmittedFeedback(new Set());
    setPurging(false);
    alert(`Cleared ${count} feedback reviews. Game data is intact.`);
  }, []);

  const toggleVariant = (key: string) => {
    setSelectedVariants(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Helper: convert selected variant keys to ArenaVariant[]
  const buildVariantsFromSelection = (keys: Iterable<string>): ArenaVariant[] => {
    const variants: ArenaVariant[] = [];
    for (const key of keys) {
      const chip = ALL_ARENA_CHIPS.find(c => c.key === key);
      if (chip) {
        variants.push({ model: chip.model, label: chip.label, timeLimit: chip.timeLimit, timeFrame: chip.timeFrame });
      }
    }
    return variants;
  };

  const handleStart = useCallback(() => {
    if (stopCooldownRef.current) return;
    const variants = buildVariantsFromSelection(selectedVariants);
    if (variants.length < 2) return;
    arenaRef.current.startTournament(variants, gamesPerMatchup);
  }, [selectedVariants, gamesPerMatchup]);

  const handlePauseResume = () => {
    if (arenaState.status === 'running') arenaRef.current.pause();
    else if (arenaState.status === 'paused') arenaRef.current.resume();
  };

  const handleStop = () => {
    arenaRef.current.stop();
    stopCooldownRef.current = true;
    setTimeout(() => { stopCooldownRef.current = false; }, 1500);
  };
  const handleReset = () => arenaRef.current.reset();

  const handleStartContinuous = useCallback(() => {
    if (stopCooldownRef.current) return;
    const variants = buildVariantsFromSelection(ALL_ARENA_CHIPS.map(c => c.key));
    arenaRef.current.startContinuousPlay(variants, concurrency);
  }, [concurrency]);

  const isContinuous = arenaState.status === 'continuous';
  const isRunning = arenaState.status === 'running' || arenaState.status === 'paused' || isContinuous;
  const progress = arenaState.totalMatchups > 0
    ? Math.round((arenaState.completedMatchups / arenaState.totalMatchups) * 100)
    : 0;

  // Aggregate blunders by variant for the summary table
  const blundersByVariant: Record<string, BlunderRecord[]> = {};
  for (const b of arenaState.blunders) {
    if (!blundersByVariant[b.variantLabel]) blundersByVariant[b.variantLabel] = [];
    blundersByVariant[b.variantLabel].push(b);
  }

  const sortedRatings = useMemo(() => {
    return [...arenaState.ratings].sort((a, b) => {
      let aVal: number, bVal: number;
      switch (lbSort.col) {
        case 'wins': aVal = a.wins; bVal = b.wins; break;
        case 'losses': aVal = a.losses; bVal = b.losses; break;
        case 'draws': aVal = a.draws; bVal = b.draws; break;
        case 'games': aVal = a.games; bVal = b.games; break;
        case 'winRate':
          aVal = a.games > 0 ? a.wins / a.games : -1;
          bVal = b.games > 0 ? b.wins / b.games : -1;
          break;
        case 'blunders': aVal = a.blunders; bVal = b.blunders; break;
        default: aVal = a.rating; bVal = b.rating;
      }
      return lbSort.dir === 'desc' ? bVal - aVal : aVal - bVal;
    });
  }, [arenaState.ratings, lbSort]);

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <button className={styles.backButton} onClick={() => { playUISound(); navigate('/game'); }}>
          Back
        </button>
        <h1 className={styles.title}>AI Test Arena</h1>
        <span className={styles.subtitle}>Round-robin tournament with ELO ranking</span>
      </div>

      <div className={styles.content}>
        <div className={styles.contentMain}>
        {/* Config Panel */}
        <div className={styles.panel}>
          <h2 className={styles.panelTitle}>Tournament Config</h2>

          {Object.values(TimeFrame).map(tf => (
            <div key={tf} className={styles.configSection}>
              <label className={styles.label}>
                {TIME_FRAME_CONFIG[tf].displayName} ({TIME_FRAME_CONFIG[tf].timeLimit / 1000}s)
              </label>
              <div className={styles.chipGrid}>
                {CHIPS_BY_TIME[tf].map(chip => (
                  <button
                    key={chip.key}
                    className={`${styles.chip} ${selectedVariants.has(chip.key) ? styles.chipActive : ''}`}
                    onClick={() => toggleVariant(chip.key)}
                    disabled={isRunning}
                  >
                    {chip.label}
                  </button>
                ))}
              </div>
            </div>
          ))}

          <div className={styles.configRow}>
            <div className={styles.configSection}>
              <label className={styles.label}>Games per matchup (tournament)</label>
              <input
                type="number"
                min={1}
                max={50}
                value={gamesPerMatchup}
                onChange={e => setGamesPerMatchup(Math.max(1, parseInt(e.target.value) || 1))}
                className={styles.numberInput}
                disabled={isRunning}
              />
            </div>
            <div className={styles.configSection}>
              <label className={styles.label}>Concurrent games (random)</label>
              <div className={styles.chipGrid}>
                {[1, 2, 3, 4].map(n => (
                  <button
                    key={n}
                    className={`${styles.chip} ${concurrency === n ? styles.chipActive : ''}`}
                    onClick={() => setConcurrency(n)}
                    disabled={isRunning}
                  >
                    {n}x
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className={styles.controls}>
            {!isRunning && arenaState.status !== 'finished' && (
              <>
                <button
                  className={styles.startButton}
                  onClick={handleStart}
                  disabled={selectedVariants.size < 2}
                >
                  Start Tournament
                </button>
                <button
                  className={styles.continuousButton}
                  onClick={handleStartContinuous}
                >
                  Start Random Games
                </button>
              </>
            )}
            {isRunning && (
              <>
                <button className={styles.pauseButton} onClick={handlePauseResume}>
                  {arenaState.status === 'paused' ? 'Resume' : 'Pause'}
                </button>
                <button className={styles.stopButton} onClick={handleStop}>
                  Stop
                </button>
                {isContinuous && (
                  <span className={styles.continuousLabel}>
                    {arenaState.currentGameInMatchup}x lanes - {arenaState.completedMatchups} games played
                  </span>
                )}
              </>
            )}
            {(arenaState.status === 'finished' || (!isRunning && arenaState.matchHistory.length > 0)) && (
              <button className={styles.resetButton} onClick={handleReset}>
                Reset
              </button>
            )}
          </div>
        </div>

        {/* Live Status */}
        {(isRunning || arenaState.status === 'finished') && (
          <div className={styles.panel}>
            <h2 className={styles.panelTitle}>
              {arenaState.status === 'finished' ? 'Tournament Complete'
                : isContinuous ? 'Continuous Play'
                : 'Live Status'}
            </h2>
            <div className={styles.statusRow}>
              <span className={styles.statusLabel}>Current:</span>
              <span className={styles.statusValue}>
                {arenaState.currentMatchup}
                {arenaState.currentMoveCount > 0 && ` (move ${arenaState.currentMoveCount})`}
              </span>
            </div>
            <div className={styles.statusRow}>
              <span className={styles.statusLabel}>
                {isContinuous ? 'Games:' : 'Progress:'}
              </span>
              <span className={styles.statusValue}>
                {isContinuous
                  ? `${arenaState.completedMatchups} games completed`
                  : `${arenaState.completedMatchups} / ${arenaState.totalMatchups} games`}
              </span>
            </div>
            {!isContinuous && (
              <div className={styles.progressBar}>
                <div className={styles.progressFill} style={{ width: `${progress}%` }} />
              </div>
            )}
          </div>
        )}

        {/* ELO Leaderboard */}
        {sortedRatings.length > 0 && (
          <div className={styles.panel}>
            <h2 className={styles.panelTitle}>ELO Leaderboard</h2>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Variant</th>
                  <th className={styles.sortableHeader} onClick={() => handleLbSort('elo')}>ELO{sortArrow('elo', lbSort)}</th>
                  <th className={styles.sortableHeader} onClick={() => handleLbSort('wins')}>W{sortArrow('wins', lbSort)}</th>
                  <th className={styles.sortableHeader} onClick={() => handleLbSort('losses')}>L{sortArrow('losses', lbSort)}</th>
                  <th className={styles.sortableHeader} onClick={() => handleLbSort('draws')}>D{sortArrow('draws', lbSort)}</th>
                  <th className={styles.sortableHeader} onClick={() => handleLbSort('games')}>Games{sortArrow('games', lbSort)}</th>
                  <th className={styles.sortableHeader} onClick={() => handleLbSort('winRate')}>Win%{sortArrow('winRate', lbSort)}</th>
                  <th className={styles.sortableHeader} onClick={() => handleLbSort('blunders')}>Blunders{sortArrow('blunders', lbSort)}</th>
                </tr>
              </thead>
              <tbody>
                {sortedRatings.map((r, i) => {
                  const winRate = r.games > 0 ? ((r.wins / r.games) * 100).toFixed(0) : '-';
                  const chipKey = ALL_ARENA_CHIPS.find(c => c.label === r.variant.label)?.key || null;
                  return (
                    <tr
                      key={r.variant.label}
                      className={`${i === 0 ? styles.topRow : ''} ${chipKey && selectedProfileId === chipKey ? styles.selectedRow : ''}`}
                      style={{ cursor: chipKey ? 'pointer' : undefined }}
                      onClick={() => chipKey && loadProfileData(chipKey)}
                    >
                      <td>{i + 1}</td>
                      <td className={styles.variantName}>{r.variant.label}</td>
                      <td className={styles.elo}>{Math.round(r.rating)}</td>
                      <td className={styles.wins}>{r.wins}</td>
                      <td className={styles.losses}>{r.losses}</td>
                      <td>{r.draws}</td>
                      <td>{r.games}</td>
                      <td>{winRate}%</td>
                      <td className={r.blunders > 0 ? styles.blunderCount : ''}>{r.blunders}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Blunder Report */}
        {arenaState.blunders.length > 0 && (
          <div className={styles.panel}>
            <h2 className={styles.panelTitle}>Blunder Report</h2>
            <div className={styles.blunderSummary}>
              {Object.entries(blundersByVariant)
                .sort(([, a], [, b]) => b.length - a.length)
                .map(([variant, blunders]) => (
                  <div key={variant} className={styles.blunderGroup}>
                    <button
                      className={styles.blunderHeader}
                      onClick={() =>
                        setExpandedBlunderVariant(expandedBlunderVariant === variant ? null : variant)
                      }
                    >
                      <span className={styles.blunderVariant}>{variant}</span>
                      <span className={styles.blunderBadge}>{blunders.length} blunders</span>
                      <span className={styles.expandIcon}>
                        {expandedBlunderVariant === variant ? '-' : '+'}
                      </span>
                    </button>
                    {expandedBlunderVariant === variant && (
                      <div className={styles.blunderDetails}>
                        {blunders.map((b, i) => (
                          <div key={i} className={styles.blunderItem}>
                            <span>Game #{b.gameIndex + 1}, Move {b.moveNumber}</span>
                            <span className={styles.blunderConfidence}>
                              conf: {(b.confidence * 100).toFixed(0)}%
                            </span>
                            <span className={styles.blunderOpponent}>vs {b.opponent}</span>
                            <div className={styles.blunderReasoning}>{b.reasoning}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Match History */}
        {arenaState.matchHistory.length > 0 && (
          <div className={styles.panel}>
            <h2 className={styles.panelTitle}>
              Match History ({arenaState.matchHistory.length} games)
            </h2>
            <div className={styles.matchHistory}>
              {arenaState.matchHistory.map((m, i) => (
                <div key={i} className={styles.matchRow}>
                  <span className={styles.matchIndex}>#{i + 1}</span>
                  <span className={m.winner === 'light' ? styles.matchWinner : styles.matchLoser}>
                    {m.lightVariant}
                  </span>
                  <span className={styles.matchVs}>vs</span>
                  <span className={m.winner === 'dark' ? styles.matchWinner : styles.matchLoser}>
                    {m.darkVariant}
                  </span>
                  <span className={styles.matchMoves}>{m.moves} moves</span>
                  <span className={styles.matchResult}>
                    {m.winner === 'draw' ? 'Draw' : m.winner === 'light' ? 'Light wins' : 'Dark wins'}
                  </span>
                  {m.fenHistory && m.fenHistory.length > 1 && (
                    <button
                      className={styles.refreshButton}
                      style={{ marginLeft: 8, padding: '2px 8px', fontSize: '0.75rem' }}
                      onClick={() => {
                        const record = matchResultToReplayRecord(m, i);
                        if (record) setReplayGame(record);
                      }}
                    >
                      Replay
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* === Firebase Persistent Data === */}

        {/* All-Time Ratings */}
        <div className={styles.panel}>
          <h2 className={styles.panelTitle}>
            All-Time Ratings (Firebase)
            <button className={styles.refreshButton} onClick={loadFirebaseRatings}>
              Refresh
            </button>
            {/* TODO: Re-enable Purge All button if needed for schema migrations
            <button className={styles.refreshButton} onClick={async () => {
              if (!window.confirm('Purge ALL Firebase arena data? (ratings, games, tournaments, feedback)')) return;
              const counts = await AIArenaFirebaseService.purgeAllData();
              alert(`Purged: ${counts.ratings} ratings, ${counts.games} games, ${counts.tournaments} tournaments, ${counts.feedback} feedback`);
              loadFirebaseRatings();
            }}>
              Purge All
            </button>
            */}
          </h2>
          {firebaseLoading ? (
            <div className={styles.statusRow}>
              <span className={styles.statusValue}>Loading...</span>
            </div>
          ) : (() => {
            // Merge all 21 chips with Firebase data, showing placeholders for missing variants
            const fbMap = new Map(firebaseRatings.map(r => [r._docId, r]));
            const allRows = ALL_ARENA_CHIPS.map(chip => {
              const fb = fbMap.get(chip.key);
              return {
                key: chip.key,
                displayName: chip.label,
                rating: fb?.rating ?? 1300,
                wins: fb?.wins ?? 0,
                losses: fb?.losses ?? 0,
                draws: fb?.draws ?? 0,
                games: fb?.games ?? 0,
                drawRate: fb?.drawRate ?? 0,
                avgGameLength: fb?.avgGameLength ?? 0,
                avgBlundersPerGame: fb?.avgBlundersPerGame ?? 0,
                avgBrilliantsPerGame: fb?.avgBrilliantsPerGame ?? 0,
                hasData: !!fb,
              };
            }).sort((a, b) => {
              let aVal: number, bVal: number;
              switch (fbSort.col) {
                case 'wins': aVal = a.wins; bVal = b.wins; break;
                case 'losses': aVal = a.losses; bVal = b.losses; break;
                case 'draws': aVal = a.draws; bVal = b.draws; break;
                case 'games': aVal = a.games; bVal = b.games; break;
                case 'drawRate': aVal = a.drawRate; bVal = b.drawRate; break;
                case 'avgLen': aVal = a.avgGameLength; bVal = b.avgGameLength; break;
                case 'blundPerGame': aVal = a.avgBlundersPerGame; bVal = b.avgBlundersPerGame; break;
                case 'brillPerGame': aVal = a.avgBrilliantsPerGame; bVal = b.avgBrilliantsPerGame; break;
                default: aVal = a.rating; bVal = b.rating;
              }
              const cmp = fbSort.dir === 'desc' ? bVal - aVal : aVal - bVal;
              return cmp !== 0 ? cmp : a.displayName.localeCompare(b.displayName);
            });
            return (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Variant</th>
                  <th className={styles.sortableHeader} onClick={() => handleFbSort('elo')}>ELO{sortArrow('elo', fbSort)}</th>
                  <th className={styles.sortableHeader} onClick={() => handleFbSort('wins')}>W{sortArrow('wins', fbSort)}</th>
                  <th className={styles.sortableHeader} onClick={() => handleFbSort('losses')}>L{sortArrow('losses', fbSort)}</th>
                  <th className={styles.sortableHeader} onClick={() => handleFbSort('draws')}>D{sortArrow('draws', fbSort)}</th>
                  <th className={styles.sortableHeader} onClick={() => handleFbSort('games')}>Games{sortArrow('games', fbSort)}</th>
                  <th className={styles.sortableHeader} onClick={() => handleFbSort('drawRate')}>Draw%{sortArrow('drawRate', fbSort)}</th>
                  <th className={styles.sortableHeader} onClick={() => handleFbSort('avgLen')}>Avg Len{sortArrow('avgLen', fbSort)}</th>
                  <th className={styles.sortableHeader} onClick={() => handleFbSort('blundPerGame')}>Blund/G{sortArrow('blundPerGame', fbSort)}</th>
                  <th className={styles.sortableHeader} onClick={() => handleFbSort('brillPerGame')}>Brill/G{sortArrow('brillPerGame', fbSort)}</th>
                </tr>
              </thead>
              <tbody>
                {allRows.map((r, i) => (
                    <tr
                      key={r.key}
                      className={`${i === 0 && r.hasData ? styles.topRow : ''} ${selectedProfileId === r.key ? styles.selectedRow : ''}`}
                      style={{ cursor: 'pointer', opacity: r.hasData ? 1 : 0.45 }}
                      onClick={() => loadProfileData(r.key)}
                    >
                      <td>{i + 1}</td>
                      <td className={styles.variantName}>{r.displayName}</td>
                      <td className={styles.elo}>{r.hasData ? Math.round(r.rating) : '-'}</td>
                      <td className={styles.wins}>{r.wins}</td>
                      <td className={styles.losses}>{r.losses}</td>
                      <td>{r.draws}</td>
                      <td>{r.games}</td>
                      <td>{r.hasData ? `${(r.drawRate * 100).toFixed(0)}%` : '-'}</td>
                      <td>{r.hasData ? Math.round(r.avgGameLength) : '-'}</td>
                      <td className={r.avgBlundersPerGame > 1 ? styles.blunderCount : ''}>
                        {r.hasData ? r.avgBlundersPerGame.toFixed(1) : '-'}
                      </td>
                      <td className={r.hasData ? styles.brilliantCount : ''}>
                        {r.hasData ? r.avgBrilliantsPerGame.toFixed(1) : '-'}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
            );
          })()}
        </div>

        {/* Head-to-Head Lookup */}
        {firebaseRatings.length >= 2 && (
          <div className={styles.panel}>
            <h2 className={styles.panelTitle}>Head-to-Head Lookup</h2>
            <div className={styles.h2hControls}>
              <select
                className={styles.h2hSelect}
                value={h2hVariantA}
                onChange={e => { setH2hVariantA(e.target.value); setH2hResult(null); }}
              >
                <option value="">Select variant A</option>
                {firebaseRatings.map(r => (
                  <option key={r.displayName} value={r._docId}>
                    {r.displayName}
                  </option>
                ))}
              </select>
              <span className={styles.h2hVs}>vs</span>
              <select
                className={styles.h2hSelect}
                value={h2hVariantB}
                onChange={e => { setH2hVariantB(e.target.value); setH2hResult(null); }}
              >
                <option value="">Select variant B</option>
                {firebaseRatings.map(r => (
                  <option key={r.displayName} value={r._docId}>
                    {r.displayName}
                  </option>
                ))}
              </select>
              <button
                className={styles.h2hButton}
                onClick={handleH2hLookup}
                disabled={!h2hVariantA || !h2hVariantB || h2hVariantA === h2hVariantB}
              >
                Lookup
              </button>
            </div>
            {h2hResult && (
              <div className={styles.h2hResult}>
                <span className={styles.wins}>A wins: {h2hResult.aWins}</span>
                <span className={styles.losses}>B wins: {h2hResult.bWins}</span>
                <span>Draws: {h2hResult.draws}</span>
              </div>
            )}
          </div>
        )}
        </div>{/* end contentMain */}

        {/* === Right column === */}
        <div className={styles.contentSide}>

        {/* === Model Profile Panel === */}
        {selectedProfileId && (
          <div className={styles.panel}>
            {/* Header */}
            <div className={styles.profileHeader}>
              <button className={styles.backButton} onClick={() => { playUISound(); setSelectedProfileId(null); setProfileGames([]); }}>
                Back
              </button>
              <span className={styles.profileName}>
                {selectedProfileRating?.displayName || NAMED_VARIANTS[selectedProfileId] || selectedProfileId}
              </span>
              <span className={styles.profileVariantId}>{resolveVariantDescriptor(selectedProfileId)}</span>
            </div>

            {profileLoading ? (
              <div className={styles.statusRow}>
                <span className={styles.statusValue}>Loading profile...</span>
              </div>
            ) : selectedProfileRating ? (
              <>
                {/* Stats Grid */}
                <div className={styles.profileStatsGrid}>
                  <div className={styles.profileStat}>
                    <span className={styles.profileStatLabel}>ELO Rating</span>
                    <span className={`${styles.profileStatValue} ${styles.elo}`}>{Math.round(selectedProfileRating.rating)}</span>
                  </div>
                  <div className={styles.profileStat}>
                    <span className={styles.profileStatLabel}>Record</span>
                    <span className={styles.profileStatValue}>
                      <span className={styles.wins}>{selectedProfileRating.wins}W</span>
                      {' / '}
                      <span className={styles.losses}>{selectedProfileRating.losses}L</span>
                      {' / '}
                      {selectedProfileRating.draws}D
                    </span>
                  </div>
                  <div className={styles.profileStat}>
                    <span className={styles.profileStatLabel}>Games</span>
                    <span className={styles.profileStatValue}>{selectedProfileRating.games}</span>
                  </div>
                  <div className={styles.profileStat}>
                    <span className={styles.profileStatLabel}>Win Rate</span>
                    <span className={styles.profileStatValue}>
                      {selectedProfileRating.games > 0 ? ((selectedProfileRating.wins / selectedProfileRating.games) * 100).toFixed(0) : 0}%
                    </span>
                  </div>
                  <div className={styles.profileStat}>
                    <span className={styles.profileStatLabel}>Avg Game Length</span>
                    <span className={styles.profileStatValue}>{Math.round(selectedProfileRating.avgGameLength)} moves</span>
                  </div>
                  <div className={styles.profileStat}>
                    <span className={styles.profileStatLabel}>Blunders/Game</span>
                    <span className={`${styles.profileStatValue} ${selectedProfileRating.avgBlundersPerGame > 1 ? styles.blunderCount : ''}`}>
                      {selectedProfileRating.avgBlundersPerGame.toFixed(2)}
                    </span>
                  </div>
                  <div className={styles.profileStat}>
                    <span className={styles.profileStatLabel}>Brilliants/Game</span>
                    <span className={`${styles.profileStatValue} ${styles.brilliantCount}`}>
                      {selectedProfileRating.avgBrilliantsPerGame.toFixed(2)}
                    </span>
                  </div>
                  <div className={styles.profileStat}>
                    <span className={styles.profileStatLabel}>Draw Rate</span>
                    <span className={styles.profileStatValue}>{(selectedProfileRating.drawRate * 100).toFixed(0)}%</span>
                  </div>
                </div>

                {/* Rating Trend Chart */}
                {selectedProfileRating.ratingHistory.length > 1 && (() => {
                  const history = selectedProfileRating.ratingHistory;
                  const ratings = history.map(h => h.rating);
                  const minR = Math.min(...ratings);
                  const maxR = Math.max(...ratings);
                  const range = maxR - minR || 1;
                  const W = 600, H = 120, PAD = 30;
                  const plotW = W - PAD * 2, plotH = H - 20;
                  const points = history.map((h, i) => {
                    const x = PAD + (i / (history.length - 1)) * plotW;
                    const y = 10 + plotH - ((h.rating - minR) / range) * plotH;
                    return `${x},${y}`;
                  });
                  return (
                    <div className={styles.profileChart}>
                      <span className={styles.profileStatLabel}>Rating Trend</span>
                      <svg viewBox={`0 0 ${W} ${H}`} className={styles.profileSvg}>
                        <polyline
                          points={points.join(' ')}
                          fill="none"
                          stroke="#3b82f6"
                          strokeWidth="2"
                        />
                        {history.map((h, i) => {
                          const x = PAD + (i / (history.length - 1)) * plotW;
                          const y = 10 + plotH - ((h.rating - minR) / range) * plotH;
                          return <circle key={i} cx={x} cy={y} r="3" fill="#60a5fa" />;
                        })}
                        <text x={PAD - 4} y={14} textAnchor="end" fill="#64748b" fontSize="10">{Math.round(maxR)}</text>
                        <text x={PAD - 4} y={H - 6} textAnchor="end" fill="#64748b" fontSize="10">{Math.round(minR)}</text>
                        <text x={PAD} y={H - 2} fill="#475569" fontSize="9">{history[0].gameCount}g</text>
                        <text x={W - PAD} y={H - 2} textAnchor="end" fill="#475569" fontSize="9">{history[history.length - 1].gameCount}g</text>
                      </svg>
                    </div>
                  );
                })()}

                {/* Piece Selection Adaptation */}
                <div className={styles.piecePreferenceSection}>
                  {(() => {
                    const ps = pieceSelectionStats;
                    if (!ps || ps.totalGames === 0) {
                      return (
                        <>
                          <span className={styles.profileStatLabel}>Piece Selection Adaptation</span>
                          <div style={{ color: '#475569', fontSize: 12, padding: '8px 0' }}>
                            No data yet — play some games to see preferences develop.
                          </div>
                        </>
                      );
                    }

                    // Build sorted piece arrays with UCB1 pick probabilities
                    const totalG = ps.totalGames;

                    // King entries with UCB1 pick probability
                    const kingC = totalG > 0 ? Math.max(0.1, 2.0 / Math.sqrt(totalG)) : 2.0;
                    const kingRaw = Object.entries(ps.pieceStats)
                      .filter(([, s]) => s.gamesAsKing > 0)
                      .map(([name, s]) => {
                        const winRate = s.winsAsKing / s.gamesAsKing;
                        const score = totalG > 1
                          ? winRate + kingC * Math.sqrt(Math.log(totalG) / s.gamesAsKing)
                          : winRate + 1;
                        return { name, games: s.gamesAsKing, wins: s.winsAsKing, winRate, score };
                      });
                    const kingScoreSum = kingRaw.reduce((s, k) => s + Math.max(0.01, k.score), 0);
                    const kingEntries = kingRaw
                      .map(k => ({ ...k, pickPct: Math.max(0.01, k.score) / kingScoreSum }))
                      .sort((a, b) => b.pickPct - a.pickPct || b.games - a.games);

                    // Army entries with UCB1 pick probability
                    const pieceC = totalG > 0 ? Math.max(0.05, 1.5 / Math.sqrt(totalG)) : 1.5;
                    const armyRaw = Object.entries(ps.pieceStats)
                      .filter(([, s]) => s.gamesInArmy > 0)
                      .map(([name, s]) => {
                        const winRate = s.winsInArmy / s.gamesInArmy;
                        const survivalRate = s.survivalCount / s.gamesInArmy;
                        let score = totalG > 1
                          ? winRate + pieceC * Math.sqrt(Math.log(totalG) / s.gamesInArmy)
                          : winRate + 1;
                        score += survivalRate * 0.1;
                        return { name, games: s.gamesInArmy, wins: s.winsInArmy, winRate, survivalRate, score };
                      });
                    const armyScoreSum = armyRaw.reduce((s, p) => s + Math.max(0.01, p.score), 0);
                    const armyEntries = armyRaw
                      .map(p => ({ ...p, pickPct: Math.max(0.01, p.score) / armyScoreSum }))
                      .sort((a, b) => b.pickPct - a.pickPct || b.games - a.games);

                    // Top synergy pairs (min 3 games)
                    const synergyEntries = Object.entries(ps.synergyStats)
                      .filter(([, s]) => s.games >= 3)
                      .map(([key, s]) => ({
                        pair: key,
                        games: s.games,
                        wins: s.wins,
                        winRate: s.wins / s.games,
                      }))
                      .sort((a, b) => b.winRate - a.winRate)
                      .slice(0, 5);

                    return (
                      <>
                        <span className={styles.profileStatLabel}>
                          Piece Selection Adaptation ({ps.totalGames} games learned)
                        </span>
                        <span style={{ color: '#475569', fontSize: 10, display: 'block', marginBottom: 8 }}>
                          Shared across all time frames for this model
                        </span>

                        {/* King Preferences */}
                        {kingEntries.length > 0 && (
                          <div className={styles.profileBars}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                              <span style={{ color: '#94a3b8', fontSize: 11, fontWeight: 600 }}>
                                King Preferences
                              </span>
                              <span style={{ color: '#475569', fontSize: 9 }}>
                                win% &middot; games &middot; pick%
                              </span>
                            </div>
                            {kingEntries.map(k => {
                              const aboveAvg = k.pickPct > 1 / 10;
                              return (
                                <div key={k.name} className={styles.profileBarRow}>
                                  <span className={styles.profileBarLabel}>{k.name}</span>
                                  <div className={styles.profileBarTrack}>
                                    <div
                                      className={styles.profileBarFill}
                                      style={{
                                        width: `${Math.round(k.winRate * 100)}%`,
                                        background: k.winRate >= 0.6 ? '#22c55e' : k.winRate >= 0.4 ? '#eab308' : '#ef4444',
                                      }}
                                    />
                                  </div>
                                  <span className={styles.profileBarValue}>
                                    {Math.round(k.winRate * 100)}%
                                  </span>
                                  <span style={{ color: '#475569', fontSize: 10, minWidth: 24 }}>
                                    {k.games}g
                                  </span>
                                  <span style={{
                                    color: aboveAvg ? '#38bdf8' : '#64748b',
                                    fontSize: 10,
                                    fontWeight: 600,
                                    minWidth: 38,
                                    textAlign: 'right',
                                  }}>
                                    {aboveAvg ? '↑' : '↓'}{Math.round(k.pickPct * 100)}%
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* Army Piece Preferences */}
                        {armyEntries.length > 0 && (
                          <div className={styles.profileBars}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                              <span style={{ color: '#94a3b8', fontSize: 11, fontWeight: 600 }}>
                                Piece Win Rates (in army)
                              </span>
                              <span style={{ color: '#475569', fontSize: 9 }}>
                                win% &middot; games &middot; pick%
                              </span>
                            </div>
                            {armyEntries.map(p => {
                              const aboveAvg = p.pickPct > 1 / 11;
                              return (
                                <div key={p.name} className={styles.profileBarRow}>
                                  <span className={styles.profileBarLabel}>{p.name}</span>
                                  <div className={styles.profileBarTrack}>
                                    <div
                                      className={styles.profileBarFill}
                                      style={{
                                        width: `${Math.round(p.winRate * 100)}%`,
                                        background: p.winRate >= 0.6 ? '#22c55e' : p.winRate >= 0.4 ? '#eab308' : '#ef4444',
                                      }}
                                    />
                                  </div>
                                  <span className={styles.profileBarValue}>
                                    {Math.round(p.winRate * 100)}%
                                  </span>
                                  <span style={{ color: '#475569', fontSize: 10, minWidth: 24 }}>
                                    {p.games}g
                                  </span>
                                  <span style={{
                                    color: aboveAvg ? '#38bdf8' : '#64748b',
                                    fontSize: 10,
                                    fontWeight: 600,
                                    minWidth: 38,
                                    textAlign: 'right',
                                  }}>
                                    {aboveAvg ? '↑' : '↓'}{Math.round(p.pickPct * 100)}%
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* Top Synergies */}
                        {synergyEntries.length > 0 && (
                          <div style={{ marginTop: 8 }}>
                            <span style={{ color: '#94a3b8', fontSize: 11, fontWeight: 600, marginBottom: 4, display: 'block' }}>
                              Top Synergies
                            </span>
                            {synergyEntries.map(s => (
                              <div key={s.pair} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0', fontSize: 12, color: '#e2e8f0' }}>
                                <span style={{ fontWeight: 500 }}>{s.pair.replace('+', ' + ')}</span>
                                <span style={{ color: '#475569' }}>&mdash;</span>
                                <span style={{ color: s.winRate >= 0.6 ? '#4ade80' : s.winRate >= 0.4 ? '#facc15' : '#f87171', fontWeight: 600 }}>
                                  {Math.round(s.winRate * 100)}%
                                </span>
                                <span style={{ color: '#475569', fontSize: 10 }}>({s.games} games)</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>

                {/* Recent Games */}
                <div>
                  <span className={styles.profileStatLabel}>Recent Games ({profileGames.length})</span>
                  <div className={styles.matchHistory} style={{ maxHeight: 240 }}>
                    {profileGames.map((g, i) => {
                      const isLight = g.lightVariant === selectedProfileId;
                      const opponent = isLight ? g.darkVariant : g.lightVariant;
                      const opponentName = NAMED_VARIANTS[opponent] || opponent;
                      const won = (isLight && g.winner === 'light') || (!isLight && g.winner === 'dark');
                      const drew = g.winner === 'draw';
                      return (
                        <div key={g._id || i} className={styles.matchRow}>
                          <span style={{ color: won ? '#4ade80' : drew ? '#94a3b8' : '#f87171', fontWeight: 600, minWidth: 36 }}>
                            {won ? 'W' : drew ? 'D' : 'L'}
                          </span>
                          <span style={{ color: '#94a3b8', fontSize: 11 }}>vs</span>
                          <span className={styles.variantName} style={{ fontSize: 12 }}>{opponentName}</span>
                          <span className={styles.matchMoves}>{g.moveCount} moves</span>
                          <span className={styles.matchResult}>{formatWinCondition(g.winCondition)}</span>
                          {g.fenHistory && g.fenHistory.length > 1 && (
                            <button
                              className={styles.refreshButton}
                              style={{ marginLeft: 4, padding: '2px 8px', fontSize: '0.7rem' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                const record = aiGameToReplayRecord(g);
                                if (record) setReplayGame(record);
                              }}
                            >
                              Replay
                            </button>
                          )}
                        </div>
                      );
                    })}
                    {profileGames.length === 0 && (
                      <div className={styles.statusRow}>
                        <span className={styles.statusValue}>No games found for this variant.</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Notable Moves */}
                {(() => {
                  const notables = profileGames.flatMap(g =>
                    (g.notableMoves || [])
                      .filter(m =>
                        (g.lightVariant === selectedProfileId && m.side === 'light') ||
                        (g.darkVariant === selectedProfileId && m.side === 'dark')
                      )
                      .map(m => ({ ...m, game: g }))
                  );
                  if (notables.length === 0) return null;
                  const blunders = notables.filter(m => m.type === 'blunder');
                  const brilliants = notables.filter(m => m.type === 'brilliant');
                  return (
                    <div className={styles.profileNotableMoves}>
                      <span className={styles.profileStatLabel}>
                        Notable Moves ({blunders.length} blunders, {brilliants.length} brilliants)
                      </span>
                      {notables.slice(0, 20).map((m, i) => (
                        <div key={i} className={styles.profileNotableItem}>
                          <span style={{
                            color: m.type === 'blunder' ? '#fb923c' : '#a78bfa',
                            fontWeight: 600,
                            fontSize: 11,
                            minWidth: 52,
                          }}>
                            {m.type === 'blunder' ? 'BLUNDER' : 'BRILLIANT'}
                          </span>
                          <span style={{ fontWeight: 500, fontSize: 12 }}>
                            {m.pieceMoved} {m.from}→{m.to}
                          </span>
                          <span style={{ color: '#64748b', fontSize: 11 }}>
                            ({(m.confidence * 100).toFixed(0)}%) M{m.moveNum}
                          </span>
                          <span style={{ color: '#475569', fontSize: 11, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {m.reasoning}
                          </span>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </>
            ) : (
              <div className={styles.statusRow}>
                <span className={styles.statusValue}>No Firebase data for this variant yet.</span>
              </div>
            )}
          </div>
        )}

        {/* === Blunder Analysis Panel === */}
        <div className={styles.panel}>
          <h2 className={styles.panelTitle}>
            Blunder Analysis Lab
            <button className={styles.refreshButton} onClick={loadBlunderGames}>
              {blunderAnalysisLoading ? 'Loading...' : blunderGames.length > 0 ? 'Refresh' : 'Load Blunders'}
            </button>
            {submittedFeedback.size > 0 && (
              <button
                className={styles.refreshButton}
                onClick={handleClearFeedback}
                disabled={purging}
              >
                {purging ? '...' : 'Reset Reviews'}
              </button>
            )}
          </h2>

          {blunderGames.length > 0 && (
            <>
              {/* Filter + Stats bar */}
              <div className={styles.blunderLabBar}>
                <select
                  className={styles.h2hSelect}
                  value={blunderFilter}
                  onChange={e => { setBlunderFilter(e.target.value); setSelectedBlunder(null); }}
                >
                  <option value="all">All Variants</option>
                  {blunderVariantIds.map(id => (
                    <option key={id} value={id}>{id}</option>
                  ))}
                </select>
                <span className={styles.blunderLabStats}>
                  {allBlunders.length} blunders
                  {' | '}
                  {allBlunders.filter(b => submittedFeedback.has(`${b.gameId}-${b.move.moveNum}-${b.move.side}`)).length} reviewed
                  {' | '}
                  {allBlunders.filter(b => !submittedFeedback.has(`${b.gameId}-${b.move.moveNum}-${b.move.side}`)).length} pending
                </span>
              </div>

              <div className={styles.blunderLabLayout}>
                {/* Left: Blunder list */}
                <div className={styles.blunderLabList}>
                  {allBlunders.map((b, i) => {
                    const key = `${b.gameId}-${b.move.moveNum}-${b.move.side}`;
                    const isSelected = selectedBlunder?.gameId === b.gameId
                      && selectedBlunder?.move.moveNum === b.move.moveNum
                      && selectedBlunder?.move.side === b.move.side;
                    const isReviewed = submittedFeedback.has(key);

                    return (
                      <button
                        key={key + i}
                        className={`${styles.blunderLabItem} ${isSelected ? styles.blunderLabItemActive : ''} ${isReviewed ? styles.blunderLabItemReviewed : ''}`}
                        onClick={() => { setSelectedBlunder(b); setFeedbackNotes(''); }}
                      >
                        <span className={styles.blunderLabItemConf}>
                          {(b.move.confidence * 100).toFixed(0)}%
                        </span>
                        <span className={styles.blunderLabItemVariant}>{b.variantId}</span>
                        <span className={styles.blunderLabItemMeta}>
                          M{b.move.moveNum} · {b.move.side}
                        </span>
                        {isReviewed && <span className={styles.blunderLabItemCheck}>&#10003;</span>}
                      </button>
                    );
                  })}
                  {allBlunders.length === 0 && (
                    <div className={styles.blunderLabEmpty}>No blunders match filter</div>
                  )}
                </div>

                {/* Right: Detail card */}
                <div className={styles.blunderLabDetail}>
                  {selectedBlunder ? (
                    <>
                      <div className={styles.blunderLabDetailHeader}>
                        <span className={styles.blunderLabDetailTitle}>
                          {selectedBlunder.variantId}
                        </span>
                        <span className={styles.blunderLabDetailSide}>
                          Playing {selectedBlunder.move.side}
                        </span>
                      </div>

                      {/* Move summary */}
                      <div className={styles.blunderLabMoveBox}>
                        <span className={styles.blunderLabMovePiece}>
                          {selectedBlunder.move.pieceMoved || '?'}
                        </span>
                        <span className={styles.blunderLabMoveArrow}>
                          {selectedBlunder.move.from || '?'} → {selectedBlunder.move.to || '?'}
                        </span>
                        {selectedBlunder.move.captured && (
                          <span className={styles.blunderLabMoveCapture}>
                            captures {selectedBlunder.move.captured}
                          </span>
                        )}
                        <span className={styles.blunderLabMoveMeta}>
                          {selectedBlunder.move.moveType || 'move'}
                        </span>
                      </div>

                      <div className={styles.blunderLabDetailGrid}>
                        <div className={styles.blunderLabDetailField}>
                          <span className={styles.blunderLabDetailLabel}>Confidence</span>
                          <span className={`${styles.blunderLabDetailValue} ${styles.blunderCount}`}>
                            {(selectedBlunder.move.confidence * 100).toFixed(1)}%
                          </span>
                        </div>
                        <div className={styles.blunderLabDetailField}>
                          <span className={styles.blunderLabDetailLabel}>Move #</span>
                          <span className={styles.blunderLabDetailValue}>{selectedBlunder.move.moveNum}</span>
                        </div>
                        <div className={styles.blunderLabDetailField}>
                          <span className={styles.blunderLabDetailLabel}>Game</span>
                          <span className={styles.blunderLabDetailValue}>
                            {selectedBlunder.game.lightVariant} vs {selectedBlunder.game.darkVariant}
                          </span>
                        </div>
                        <div className={styles.blunderLabDetailField}>
                          <span className={styles.blunderLabDetailLabel}>Result</span>
                          <span className={styles.blunderLabDetailValue}>
                            {selectedBlunder.game.winner === 'draw' ? 'Draw' :
                              `${selectedBlunder.game.winner === 'light' ? selectedBlunder.game.lightVariant : selectedBlunder.game.darkVariant} wins`}
                            {' '}({selectedBlunder.game.winCondition}, {selectedBlunder.game.moveCount} moves)
                          </span>
                        </div>
                      </div>

                      <div className={styles.blunderLabDetailSection}>
                        <span className={styles.blunderLabDetailLabel}>AI Reasoning</span>
                        <div className={styles.blunderLabReasoning}>
                          {selectedBlunder.move.reasoning || 'No reasoning provided'}
                        </div>
                      </div>

                      {/* Pre/Post FEN comparison */}
                      <div className={styles.blunderLabFenPair}>
                        <div className={styles.blunderLabFenBox}>
                          <span className={styles.blunderLabDetailLabel}>Before Move</span>
                          <div className={styles.blunderLabFen}>
                            {selectedBlunder.move.preFen || selectedBlunder.move.fen || 'No FEN'}
                          </div>
                        </div>
                        <div className={styles.blunderLabFenBox}>
                          <span className={styles.blunderLabDetailLabel}>After Move</span>
                          <div className={styles.blunderLabFen}>
                            {selectedBlunder.move.postFen || 'No FEN'}
                          </div>
                        </div>
                      </div>

                      {/* Replay button */}
                      {(() => {
                        const record = aiGameToReplayRecord(selectedBlunder.game as AIGameDoc & { _id: string });
                        return record ? (
                          <button
                            className={styles.refreshButton}
                            style={{ marginBottom: 8, width: '100%' }}
                            onClick={() => setReplayGame(record)}
                          >
                            Replay Full Game
                          </button>
                        ) : null;
                      })()}

                      {/* Feedback section */}
                      {submittedFeedback.has(`${selectedBlunder.gameId}-${selectedBlunder.move.moveNum}-${selectedBlunder.move.side}`) ? (
                        <div className={styles.blunderLabFeedbackDone}>
                          Feedback submitted &#10003;
                        </div>
                      ) : (
                        <div className={styles.blunderLabFeedback}>
                          <span className={styles.blunderLabDetailLabel}>Your Verdict</span>
                          <textarea
                            className={styles.blunderLabNotes}
                            placeholder="Notes (optional) — why is this / isn't this a blunder?"
                            value={feedbackNotes}
                            onChange={e => setFeedbackNotes(e.target.value)}
                            rows={2}
                          />
                          <div className={styles.blunderLabVerdictRow}>
                            <button
                              className={`${styles.blunderLabVerdictBtn} ${styles.blunderLabVerdictAgree}`}
                              onClick={() => handleBlunderFeedback('agree')}
                            >
                              Blunder
                            </button>
                            <button
                              className={`${styles.blunderLabVerdictBtn} ${styles.blunderLabVerdictQuestion}`}
                              onClick={() => handleBlunderFeedback('questionable')}
                            >
                              Questionable
                            </button>
                            <button
                              className={`${styles.blunderLabVerdictBtn} ${styles.blunderLabVerdictNot}`}
                              onClick={() => handleBlunderFeedback('not-a-blunder')}
                            >
                              Not a Blunder
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className={styles.blunderLabEmpty}>
                      Select a blunder from the list to review it
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {blunderGames.length === 0 && !blunderAnalysisLoading && (
            <div className={styles.statusRow}>
              <span className={styles.statusValue}>
                Click "Load Blunders" to fetch games with detected blunders from Firebase
              </span>
            </div>
          )}
        </div>

          {/* Blunder Test Suite */}
          <BlunderTestSuitePanel
            refreshKey={blunderRefreshKey}
            onLoadPosition={(fen, lightKing, darkKing) => {
              setReplayGame(null);
              setStandaloneFen({ fen, lightKing, darkKing });
            }}
          />
        </div>{/* end contentSide */}

        {/* === Replay column === */}
        <div className={styles.contentReplay}>
          <ArenaReplayPanel
            gameRecord={replayGame}
            standaloneFen={replayGame ? null : standaloneFen}
            onBlunderSaved={() => setBlunderRefreshKey(k => k + 1)}
          />
        </div>
      </div>
    </div>
  );
}

export default AIArena;
